import { useQuery } from '@tanstack/react-query'
import { supabaseClient } from '~/shared/common/supabase/client'
import { useAuthStore } from '~/client/common/stores/auth'

export type ShareableListSummary = {
  id: number
  name: string | null
  slug: string
  created_at: string | null
  memberships: {
    id: number
    space_id: number | null
    deleted_at: string | null
    updated_at: string | null
  }[]
  venueGroupName: string | null
  ownerName: string | null
  lastUpdatedAt: string | null
}

type ShareableListsResponse = {
  data: ShareableListSummary[]
  count: number
}

async function fetchShareableLists(): Promise<ShareableListsResponse> {
  const {
    data: { session },
  } = await supabaseClient.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const res = await fetch('/api/listShareableLists', {
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? `Failed to load collections (${res.status})`)
  }

  return await res.json()
}

export function useShareableLists(options?: { enabled?: boolean }) {
  const isAuthReady = useAuthStore((s) => s.initialized && !!s.user)

  const { data, isLoading, error } = useQuery({
    queryKey: ['shareable-lists'],
    queryFn: fetchShareableLists,
    enabled: isAuthReady && (options?.enabled ?? true),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })

  return {
    data: data?.data ?? [],
    count: data?.count ?? 0,
    isLoading,
    error,
  } as const
}
